import { createStore } from "zustand/vanilla";
import { devtools } from "zustand/middleware";
import { useStore } from "zustand";

// Slices
import { createSliceAuth, type TSliceAuth } from "./auth/sliceAuth";
import { createSliceFiles, type TSliceFiles } from "./files/sliceFiles";
import { createSliceDeals, type TSliceDeals } from "./deals/sliceDeals";
import { createSliceHomepage, type TSliceHomepage } from "./homepage/sliceHomepage";
import { createSliceMappings, type TSliceMappings } from "./mappings/sliceMappings";
import {
    createSlicePropcodeVersions,
    type TPropcodeVersionsSlice,
} from "./propcodeVersions/slicePropcodeVersions";
import { createSliceCoAVersions, type TCoAVersionsSlice } from "./coaVersions/sliceCoAVersions";
import {
    createSliceSegmentVersions,
    type TSegmentVersionsSlice,
} from "./segmentVersions/sliceSegmentVersions";
import {
    createSliceSupportingFiles,
    type TSliceSupportingFiles,
} from "./supportingFiles/sliceSupportingFiles";
import {
    createSliceCompanySettings,
    type TSliceCompanySettings,
} from "./companySettings/sliceCompanySettings";
import { createSliceYardiImport, type TSliceYardiImport } from "./yardiImport/sliceYardiImport";
import {
    createSliceFileOperation,
    type TSliceFileOperation,
} from "./fileOperation/sliceFileOperation";

/**
 * Combined store state type
 */
export type TStoreRoot = TSliceAuth &
    TSliceFiles &
    TSliceDeals &
    TSliceHomepage &
    TSliceMappings &
    TPropcodeVersionsSlice &
    TCoAVersionsSlice &
    TSegmentVersionsSlice &
    TSliceSupportingFiles &
    TSliceCompanySettings &
    TSliceYardiImport &
    TSliceFileOperation;

/**
 * Store instance type
 */
export type TAppStore = ReturnType<typeof createStoreRoot>;

/**
 * Check for duplicate keys across slices
 */
const checkSlicesKeyForDuplicates = <T extends Record<string, unknown>>(slices: T[]) => {
    const keys = slices.map((slice) => Object.keys(slice)).flat();
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);

    if (duplicates.length > 0) {
        throw new Error(`Duplicate keys found in Zustand store slices: ${duplicates.join(", ")}`);
    }
};

/**
 * Creates the root store combining all slices.
 *
 * @example
 * ```typescript
 * const store = createStoreRoot();
 *
 * // Get state
 * const state = store.getState();
 *
 * // Subscribe to changes
 * const unsubscribe = store.subscribe((state) => {
 *   console.log('State changed:', state);
 * });
 * ```
 */
export const createStoreRoot = () => {
    const store = createStore<TStoreRoot>()(
        devtools(
            (set, get, store) => {
                // Create all slices
                const sliceAuth = createSliceAuth()(set, get, store);
                const sliceFiles = createSliceFiles()(set, get, store);
                const sliceDeals = createSliceDeals()(set, get, store);
                const sliceHomepage = createSliceHomepage()(set, get, store);
                const sliceMappings = createSliceMappings()(set, get, store);
                const slicePropcodeVersions = createSlicePropcodeVersions()(set, get, store);
                const sliceCoAVersions = createSliceCoAVersions()(set, get, store);
                const sliceSegmentVersions = createSliceSegmentVersions()(set, get, store);
                const sliceSupportingFiles = createSliceSupportingFiles()(set, get, store);
                const sliceCompanySettings = createSliceCompanySettings()(set, get, store);
                const sliceYardiImport = createSliceYardiImport()(set, get, store);
                const sliceFileOperation = createSliceFileOperation()(set, get, store);

                // Check for duplicates in development
                if (import.meta.env.DEV) {
                    checkSlicesKeyForDuplicates([
                        sliceAuth,
                        sliceFiles,
                        sliceDeals,
                        sliceHomepage,
                        sliceMappings,
                        slicePropcodeVersions,
                        sliceCoAVersions,
                        sliceSegmentVersions,
                        sliceSupportingFiles,
                        sliceCompanySettings,
                        sliceYardiImport,
                        sliceFileOperation,
                    ]);
                }

                return {
                    ...sliceAuth,
                    ...sliceFiles,
                    ...sliceDeals,
                    ...sliceHomepage,
                    ...sliceMappings,
                    ...slicePropcodeVersions,
                    ...sliceCoAVersions,
                    ...sliceSegmentVersions,
                    ...sliceSupportingFiles,
                    ...sliceCompanySettings,
                    ...sliceYardiImport,
                    ...sliceFileOperation,
                };
            },
            {
                name: "PRODA FinanceStore",
                serialize: {
                    options: {
                        map: true,
                    },
                },
            },
        ),
    );

    return store;
};

/**
 * Hook to use the store in React components.
 * Should be used with a selector for optimal performance.
 *
 * @example
 * ```typescript
 * // In component:
 * const user = useAppStore(state => state.user);
 * const setUser = useAppStore(state => state.setUser);
 * ```
 */
export const createUseAppStore = (store: TAppStore) => {
    return function useAppStore<T>(selector: (state: TStoreRoot) => T): T {
        return useStore(store, selector);
    };
};
